import type { CountryCode } from "libphonenumber-js"

import React from "react"
import { useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"

import { AppIcons, Box, Column, FormControl, Row } from "@/theme"
import { Button, SubmitButton } from "@/theme/Button"
import { AppText } from "@/theme/Text"
import { COLORS } from "@/theme/theme"
import { ActionButtons } from "./ActionButtons"

import {
  createPhoneNumberInternationalAsync,
  generateSmsVerificationCodeAsync,
} from "@/base/farmhq-api"
import { logger } from "@/base/logger"
import {
  buildPhoneCountryOptions,
  getDefaultPhoneCountry,
  getPhoneCountryValueText,
} from "@/base/phone-country-data"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import type { PhoneNumber } from "@/redux/models"
import { useIsPending } from "@/redux/requests.reducer"
import { useRootSelector } from "@/redux/useRootSelector"
import type { ParsedPhoneNumber } from "@/redux/user-phone-numbers.reducer"
import {
  getCanUserAddPhoneNumbers,
  parsePhoneNumberInput,
  useTooManyPhoneNumbers,
} from "@/redux/user-phone-numbers.reducer"
import type { ButtonProps } from "@/theme/Button"
import { ICON_SIZES } from "@/theme/theme/icon-sizes"

/**
 * User with phone number count under
 * the limit can press this to access
 * AddPhoneNumber form
 */
export function AddPhoneNumberButton({
  onPress,
  ...rest
}: ButtonProps): React.JSX.Element {
  const { t } = useTranslation("phoneNumbers")
  return (
    <Box ml={"auto"} mt={"$2"} my={"$2"}>
      <Button
        {...rest}
        IconComponent={"Add"}
        id={"addPhoneNumberLink"}
        size={"md"}
        text={t("addPhoneNumberLink")}
        onPress={onPress}
      />
    </Box>
  )
}

export interface AddPhoneNumberFormProps {
  defaultValue?: string
  onCancel: (() => void) | null
  onSuccess:
    | ((response: Pick<PhoneNumber, "id" | "serialized">) => void)
    | null
}

/**
 * TODO:DOC
 */
export function AddPhoneNumberForm({
  defaultValue,
  onCancel,
  onSuccess,
}: AddPhoneNumberFormProps): React.JSX.Element {
  const { i18n, t } = useTranslation("phoneNumbers")
  const canAdd = useRootSelector(getCanUserAddPhoneNumbers)
  const onTooManyPhoneNumbers = useTooManyPhoneNumbers()
  const { dispatch, handleError, toasts } = useRequestHelpers(
    "CreatePhoneNumberInternational",
  )
  const isLoading = useIsPending(
    "CompleteSmsVerification",
    "GenerateSmsVerificationCode",
    "CreatePhoneNumberInternational",
  )
  const [isTyping, setIsTyping] = React.useState(false)
  let initialValue = ""
  if (__DEV__ && typeof defaultValue === "string") {
    initialValue = defaultValue
  }
  const [country, setCountry] = React.useState<CountryCode>(
    getDefaultPhoneCountry,
  )
  const [numberInput, setNumberInput] = React.useState(initialValue)
  const [parsedNumber, setParsedNumber] = React.useState<ParsedPhoneNumber>(
    () => parsePhoneNumberInput(initialValue, getDefaultPhoneCountry()),
  )
  const countryOptions = React.useMemo(
    () => buildPhoneCountryOptions(i18n.language),
    [i18n.language],
  )

  const handleChangeText = (nextValue: string): void => {
    setNumberInput(nextValue)
    setIsTyping(true)
    const result = parsePhoneNumberInput(nextValue, country)
    setParsedNumber(result)
    // A leading "+" means the user typed a full international number;
    // keep the country selector in sync with what they typed.
    if (
      nextValue.trimStart().startsWith("+") &&
      typeof result.detectedCountry !== "undefined" &&
      result.detectedCountry !== country
    ) {
      setCountry(result.detectedCountry)
    }
  }

  const handleCountryChange = (nextValue: string): void => {
    const nextCountry = nextValue as CountryCode
    setCountry(nextCountry)
    setParsedNumber(parsePhoneNumberInput(numberInput, nextCountry))
  }

  const isInvalid =
    !isTyping && numberInput.length > 0 && !parsedNumber.isValid

  /**
   * When user stops typing for a full second, show error if
   * the phone number is invalid.
   */
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTyping(false)
    }, 1000)
    return () => {
      clearTimeout(timeout)
    }
  }, [numberInput])

  const handleSubmit = async () => {
    try {
      if (!isInvalid) {
        const phoneNumberE164Str = parsedNumber.phoneNumber?.number
        if (typeof phoneNumberE164Str === "undefined") {
          throw new TypeError("Undefined value for phone number")
        }
        if (!canAdd) {
          return onTooManyPhoneNumbers()
        }
        const phoneNumber = await dispatch(
          createPhoneNumberInternationalAsync({ phoneNumberE164Str }),
        )
          .unwrap()

          .then((response) => {
            if (onSuccess) {
              onSuccess(response)
            }
            return response
          })

        // When the phone number is created, send second request to generate
        // SMS verification code
        await dispatch(
          generateSmsVerificationCodeAsync({
            upnId: phoneNumber.id,
          }),
        )
          .unwrap()
          .then(() => toasts.success(t("codeSent")))
      }
    } catch (error) {
      logger.error("Error creating phone number", error)
      return handleError(error, {
        toastMessage: true,
      })
    }
    return undefined
  }

  const labelText: string = t("addPhoneNumberInputLabel", {
    ns: "phoneNumbers",
  })
  const isSubmitDisabled = isInvalid || numberInput.length === 0
  return (
    <Column space={"$4"}>
      <FormControl.Provider id={"phoneNumber"} isInvalid={isInvalid}>
        <FormControl.Label>{labelText}</FormControl.Label>
        <View style={styles.inputRow}>
          <View style={styles.countrySelect}>
            <FormControl.Select
              id={"phoneCountrySelect"}
              isInvalid={false}
              label={t("countrySelectLabel")}
              options={countryOptions}
              selectedValue={country}
              valueText={getPhoneCountryValueText(country)}
              onValueChange={handleCountryChange}
            />
          </View>
          <View style={styles.numberInput}>
            <FormControl.Input
              autoComplete={"tel-national"}
              keyboardType={"phone-pad"}
              placeholder={t("addPhoneNumberInputPlaceholder")}
              textContentType={"telephoneNumber"}
              value={numberInput}
              onChangeText={handleChangeText}
            />
          </View>
        </View>
        <Box mt={"$2"}>
          <FormControl.HelperText>
            {t("addPhoneNumberHelperText")}
          </FormControl.HelperText>
        </Box>
        <FormControl.ErrorMessage>
          {isInvalid ? t("addPhoneNumberInvalidNumber") : null}
        </FormControl.ErrorMessage>
      </FormControl.Provider>
      {typeof numberInput === "string" && parsedNumber.isValid ? (
        <Row alignItems={"center"}>
          <Box mr={"$2"}>
            <AppIcons.Success
              color={COLORS.$success[500]}
              size={ICON_SIZES.$xl}
            />
          </Box>
          <AppText fontSize={"$lg"}>
            {parsedNumber.phoneNumber.internationalPreview}
          </AppText>
        </Row>
      ) : null}

      {onCancel ? (
        <ActionButtons
          isLoading={isLoading}
          isSubmitDisabled={isSubmitDisabled}
          onPressCancel={onCancel}
          onPressSubmit={handleSubmit}
        />
      ) : (
        <SubmitButton
          isDisabled={isSubmitDisabled}
          isLoading={isLoading}
          onPress={handleSubmit}
        />
      )}
    </Column>
  )
}

const styles = StyleSheet.create({
  countrySelect: {
    flexShrink: 0,
    minWidth: 110,
    // On web the native <select> shows the full option label; cap the
    // width so it doesn't squeeze the number input out of the row.
    ...Platform.select({ web: { maxWidth: 150, width: 150 } }),
  },
  inputRow: {
    columnGap: 8,
    flexDirection: "row",
  },
  numberInput: {
    flex: 1,
  },
})
